import {
    type Message,
    type AgentMessageFragment,
    type UserMessage,
    type AgentMessage,
    isUserMessage,
    isAgentResponse,
    type AgentMessageFragmentTodoItem,
    type AgentMessageFragmentTodoGroup,
} from "@/hooks/use-stream-conversation";
import { AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";

export type GroupedMessage = UserMessage | AgentMessage | AgentTodoGroup;

export type AgentTodoGroup = {
    type: "todo-group";
    groupId: string;
    todos: AgentTodoGroupItem[];
    isContinuation: boolean;
};

export type AgentTodoGroupItem = {
    todoId: string;
    todoTitle: string;
    todoStatus: AgentResponseBlock_TodoItem_Phase;
    fragments: AgentMessageFragment[];
};

// A mapping from TODO ID to Title
type SeenTODOs = Map<string, string>;

/**
 * Groups messages by todo items
 *
 * Todos are grouped based on when they were created together.
 * The agent typically creates multiple todos at once (via todo_reset or todo_write),
 * and clears them together (via todo_clear or todo_reset).
 *
 * A TODO group never contains user messages. Agent messages that aren't part of a
 * group are returned as a stand-alone agent message with an updated ID.
 *
 * If the TODOs in a group were created in a previous group then we're marking the group as a
 * continuation of a previous group; this can happen if e.g. the agent asks for clarification on
 * how to solve a specific todo item and the user responds. The user message will split the group
 * and the 2nd todo group will be marked as a continuation of the first.
 *
 * @param messages - The messages to group
 */
function isSnapshotMode(messages: Message[]): boolean {
    for (const message of messages) {
        if (!isAgentResponse(message)) continue;
        for (const fragment of message.fragments) {
            if (fragment.type === "todoGroup") return true;
        }
    }
    return false;
}

export function groupMessagesByTodo(messages: Message[]): GroupedMessage[] {
    if (isSnapshotMode(messages)) {
        return groupMessagesByTodoSnapshot(messages);
    }
    return groupMessagesByTodoLegacy(messages);
}

function groupMessagesByTodoLegacy(messages: Message[]): GroupedMessage[] {
    const results: GroupedMessage[] = [];
    let groupIndex = 0;

    // Tracks TODO (ID → Title) that have been assigned to a group. Used to:
    //
    // - Detect continuation groups - if a TODO ID is already in this set when we
    //   create a new group, that group is continuing work from a previous group.
    // - Look up titles for TODOs. AgentResponseBlock_TodoItem don't contain titles when the phase is CLEAREDa
    //   so we need to track the tiles so they're available in subsequent TODO groups.
    //
    const seenTodos = new Map<string, string>();

    for (const message of messages) {
        if (isUserMessage(message)) {
            results.push(message);
            continue;
        }

        if (isAgentResponse(message)) {
            // Injected messages have no todo fragments — pass through directly
            if (message.injected) {
                results.push(message);
                continue;
            }
            const grouped = groupAgentMessagesByTodo(message, groupIndex, seenTodos, results);
            results.push(...grouped.results);
            groupIndex = grouped.nextGroupIndex;
        }
    }

    return results;
}

function groupAgentMessagesByTodo(
    message: AgentMessage,
    groupIndex: number,
    seenTodos: SeenTODOs,
    existingResults: GroupedMessage[],
): { results: (AgentMessage | AgentTodoGroup)[]; nextGroupIndex: number } {
    const results: (AgentMessage | AgentTodoGroup)[] = [];

    let current: AgentMessage | AgentTodoGroup | null = null;
    let nextAgentMessageId = message.id;
    let nextGroupIndex = groupIndex;

    // If there is a previous TODO group that has in-progress or pending TODOs
    // then we start a new TODO group with those and marks it as a continuation
    // of the previous group.
    const previousTodoGroup = findPreviousTodoGroup(existingResults);
    if (previousTodoGroup) {
        const inProgressOrPendingTodos = previousTodoGroup.todos
            .filter(
                ({ todoStatus }) =>
                    todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS ||
                    todoStatus === AgentResponseBlock_TodoItem_Phase.PENDING,
            )
            .map(
                (todo) =>
                    ({
                        // cloning to not update todos in the previous group.
                        ...todo,
                        // Re-setting fragments as we don't want the previous ones.
                        fragments: [],
                    }) satisfies AgentTodoGroupItem,
            );
        if (inProgressOrPendingTodos.length > 0) {
            current = {
                type: "todo-group",
                groupId: `todo-group-${nextGroupIndex}`,
                todos: inProgressOrPendingTodos,
                isContinuation: true,
            };
            nextGroupIndex += 1;
        }
    }

    for (const fragment of message.fragments) {
        // For non-todo fragments we just want to append it to the current message
        if (fragment.type !== "todoItem") {
            if (current === null) {
                current = initializeAgentMessage(fragment, nextAgentMessageId);
                nextAgentMessageId += 1;
            } else if (current.type === "agent") {
                current.fragments.push(fragment);
            } else if (current.type === "todo-group") {
                const inProgress = current.todos.find(
                    (t) => t.todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS,
                );
                const firstPending = current.todos.find(
                    (t) => t.todoStatus === AgentResponseBlock_TodoItem_Phase.PENDING,
                );
                if (inProgress) {
                    // For todo-groups we either add it to the todo that's in-progress
                    inProgress.fragments.push(fragment);
                } else if (firstPending) {
                    // Of the next pending todo - sometimes a todo will be marked as completed before the tool call to
                    // set the next todo to in progress has been made.
                    firstPending.fragments.push(fragment);
                } else {
                    // or if there are no in-progress/pending todos, we end the todo group and
                    // start an agent message instead.
                    results.push(current);
                    current = initializeAgentMessage(fragment, nextAgentMessageId);
                    nextAgentMessageId += 1;
                }
            }
            continue;
        }

        // For todo fragments we either update the existing group or create a new group
        if (current?.type === "todo-group") {
            if (fragment.title) {
                seenTodos.set(fragment.todoItemId, fragment.title);
            }
            updateTodos(current.todos, fragment, seenTodos);
            continue;
        } else {
            // If we see a pending|in-progress todo and we aren't already in a todo group,
            // then we start a new group.
            if (
                fragment.status === AgentResponseBlock_TodoItem_Phase.PENDING ||
                fragment.status === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS
            ) {
                if (current) results.push(current);
                current = {
                    type: "todo-group",
                    groupId: `todo-group-${nextGroupIndex}`,
                    todos: [initializeTodoGroupItem(fragment, seenTodos)],
                    isContinuation: false,
                };
                nextGroupIndex += 1;
                if (fragment.title) {
                    seenTodos.set(fragment.todoItemId, fragment.title);
                }
                continue;
            }

            // Otherwise se just add the fragment to the current message
            if (current) {
                current.fragments.push(fragment);
            }
        }
    }

    if (current) {
        results.push(current);
    }

    return { results, nextGroupIndex };
}

/**
 * Searches backwards in the results looking for previous TODO group
 */
function findPreviousTodoGroup(results: GroupedMessage[]): AgentTodoGroup | undefined {
    // Search backwards in the results
    for (let i = results.length - 1; i >= 0; i--) {
        const result = results[i];
        if (result.type !== "todo-group") {
            continue;
        }

        return result;
    }
    return undefined;
}

/** Updates the todos so they reflects the state of the latest todo represented by the fragment */
function updateTodos(todos: AgentTodoGroupItem[], fragment: AgentMessageFragmentTodoItem, seenTodos: SeenTODOs): void {
    // If the TODO is present in the group, we update it.
    for (const todo of todos) {
        if (todo.todoId === fragment.todoItemId) {
            // Don't update the status if it was already done (i.e. don't clear it)
            todo.todoStatus =
                todo.todoStatus !== AgentResponseBlock_TodoItem_Phase.DONE ? fragment.status : todo.todoStatus;
            // Only update title if fragment has one (CLEARED events don't include title)
            if (fragment.title) {
                todo.todoTitle = fragment.title;
            }
            return;
        }
    }

    // If the TODO isn't already present in the group, we add it, unless it's
    // marked as cleared. This would happened for a continued todo group where
    // a TODO that was completed in the previous group is marked as cleared in the next.
    if (fragment.status !== AgentResponseBlock_TodoItem_Phase.CLEARED) {
        todos.push(initializeTodoGroupItem(fragment, seenTodos));
    }
}

function initializeTodoGroupItem(fragment: AgentMessageFragmentTodoItem, seenTodos: SeenTODOs): AgentTodoGroupItem {
    // The fallback should only happen if we never saw a AgentResponseBlock_TodoItem for the TODO with a title set. which should never happen.
    const title = fragment.title || seenTodos.get(fragment.todoItemId) || "Untitled TODO";
    return {
        todoId: fragment.todoItemId,
        todoStatus: fragment.status,
        todoTitle: title,
        fragments: [],
    };
}

function initializeAgentMessage(fragment: AgentMessageFragment, nextAgentMessageId: number): AgentMessage {
    return {
        type: "agent",
        id: nextAgentMessageId,
        fragments: [fragment],
    };
}

function groupMessagesByTodoSnapshot(messages: Message[]): GroupedMessage[] {
    const results: GroupedMessage[] = [];

    // Tracks which groupIds we've already emitted, for continuation detection.
    const emittedGroupIds = new Set<string>();

    let currentTodoGroup: AgentTodoGroup | null = null;

    for (const message of messages) {
        if (isUserMessage(message)) {
            // Sub-agent messages (agentMessage fragments) should not break the
            // current todo group. They arrive via AgentMessage proto while the
            // parent agent is working on a todo item. Flushing the group here
            // would orphan subsequent tagged fragments into a standalone agent
            // message, and the small user bubble between two large todo groups
            // can be invisible due to virtualizer measurement issues.
            const isSubagentMessage = currentTodoGroup && message.fragments.every((f) => f.type === "agentMessage");
            if (isSubagentMessage) {
                results.push(message);
                continue;
            }

            // Flush current todo group before user message
            if (currentTodoGroup) {
                emittedGroupIds.add(currentTodoGroup.groupId);
                results.push(currentTodoGroup);
                currentTodoGroup = null;
            }
            results.push(message);
            continue;
        }

        if (!isAgentResponse(message)) continue;

        for (const fragment of message.fragments) {
            // Skip userInputSeen — not routable content
            if (fragment.type === "userInputSeen") continue;

            if (fragment.type === "todoGroup") {
                const snap = fragment as AgentMessageFragmentTodoGroup;

                if (currentTodoGroup && currentTodoGroup.groupId === snap.groupId) {
                    // Same group — update in place from snapshot
                    syncTodoGroupFromSnapshot(currentTodoGroup, snap.todos);
                } else {
                    // Different group or no current group
                    if (currentTodoGroup) {
                        emittedGroupIds.add(currentTodoGroup.groupId);
                        results.push(currentTodoGroup);
                    }
                    const isContinuation = emittedGroupIds.has(snap.groupId);
                    // For continuations, look up the previously flushed group so we
                    // can carry over accumulated fragments to matching items.
                    const previousGroup = isContinuation ? findLastTodoGroupById(results, snap.groupId) : undefined;
                    currentTodoGroup = {
                        type: "todo-group",
                        groupId: snap.groupId,
                        todos: snap.todos.map((t) => {
                            const prev = previousGroup?.todos.find((p) => p.todoId === t.id);
                            return {
                                todoId: t.id,
                                todoTitle: t.title,
                                todoStatus: t.phase,
                                fragments: prev ? [...prev.fragments] : [],
                            };
                        }),
                        isContinuation,
                    };
                }
                continue;
            }

            // Non-todoGroup fragment — route by tags
            const groupId = fragment.todoGroupId;
            const itemId = fragment.todoItemId;

            // Case 1: Tagged with group + item, and matches current group
            if (groupId && itemId && currentTodoGroup && currentTodoGroup.groupId === groupId) {
                const todoItem = currentTodoGroup.todos.find((t) => t.todoId === itemId);
                if (todoItem) {
                    todoItem.fragments.push(fragment);
                    continue;
                }
                // Item not found in group (removed by snapshot update) — fall through to standalone
            }

            // Case 2: Tagged with group but no item
            if (groupId && !itemId && currentTodoGroup && currentTodoGroup.groupId === groupId) {
                const inProgress = currentTodoGroup.todos.find(
                    (t) => t.todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS,
                );
                if (inProgress) {
                    inProgress.fragments.push(fragment);
                    continue;
                }
                // No in-progress item — fall through to standalone
            }

            // Case 3: Untagged or no matching group — standalone agent message
            if (currentTodoGroup) {
                emittedGroupIds.add(currentTodoGroup.groupId);
                results.push(currentTodoGroup);
                currentTodoGroup = null;
            }
            const lastResult = results[results.length - 1];
            if (lastResult && lastResult.type === "agent") {
                lastResult.fragments.push(fragment);
            } else {
                results.push({
                    type: "agent",
                    id: results.length + 1,
                    fragments: [fragment],
                });
            }
        }
    }

    // Flush remaining group
    if (currentTodoGroup) {
        results.push(currentTodoGroup);
    }

    return results;
}

function findLastTodoGroupById(results: GroupedMessage[], groupId: string): AgentTodoGroup | undefined {
    for (let i = results.length - 1; i >= 0; i--) {
        const r = results[i];
        if (r.type === "todo-group" && r.groupId === groupId) return r;
    }
    return undefined;
}

/**
 * Updates an existing AgentTodoGroup's todo items to match the latest snapshot.
 * Preserves accumulated fragments on items that still exist.
 * Adds new items, removes items no longer in the snapshot.
 */
function syncTodoGroupFromSnapshot(group: AgentTodoGroup, snapshotTodos: AgentMessageFragmentTodoGroup["todos"]): void {
    const existingByID = new Map(group.todos.map((t) => [t.todoId, t]));
    group.todos = snapshotTodos.map((snap) => {
        const existing = existingByID.get(snap.id);
        if (existing) {
            // Update status and title from snapshot, preserve accumulated fragments
            existing.todoStatus = snap.phase;
            existing.todoTitle = snap.title;
            return existing;
        }
        // New item from snapshot
        return {
            todoId: snap.id,
            todoTitle: snap.title,
            todoStatus: snap.phase,
            fragments: [],
        };
    });
}

export function calculateTodoGroupProgress(todos: AgentTodoGroupItem[]): { started: number; total: number } {
    const total = todos.length;
    const started = todos.filter(
        (t) =>
            t.todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS ||
            t.todoStatus === AgentResponseBlock_TodoItem_Phase.DONE,
    ).length;
    return { started, total };
}
